import * as Sentry from '@sentry/react'
import type { Query, QueryKey } from '@tanstack/react-query'

import { isMissingTenantConfig } from '~/hooks/tanstack-query/optimus/shared/apiWrapper'

/** Query keys start with a static name. Generated Optimus view queries start with `['view', name]`. */
function queryName([first, second]: QueryKey): string {
    if (first === 'view' && typeof second === 'string') {
        return `view.${second}`
    }

    return typeof first === 'string' ? first : 'unnamed'
}

/**
 * Reports a failed query to Sentry once, after TanStack Query has given up retrying it.
 *
 * Registered as the `QueryCache` `onError` callback, it runs once per failed fetch of a query, however many
 * components observe that query, and a retry that succeeds reports nothing. Errors thrown in a `select` function
 * happen in the observer, outside the cache, and are not reported anywhere, so `select` must not throw: validate
 * or map a response that can fail inside the query function.
 *
 * The fingerprint adds the query's name to Sentry's default grouping: most queries throw from a shared fetch helper,
 * so the stack alone would put different queries into one issue.
 */
export function reportQueryError(error: unknown, query: Query<unknown, unknown, unknown>): void {
    const name = queryName(query.queryKey)
    Sentry.captureException(error, {
        level: isMissingTenantConfig(error) ? 'warning' : 'error',
        fingerprint: ['{{ default }}', name],
        tags: { query: name },
    })
}
